import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useToast } from '@/context/ToastContext';
import { login as loginApi, logoutApi, googleLogin as googleLoginApi, verify2FA as verify2FAApi } from '@/api';
import { usePushNotifications, disablePushNotifications } from '@/hooks/usePushNotifications';

const INACTIVITY_LIMIT_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

export type UserRole = 'acheteur' | 'vendeur' | 'admin' | null;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatar?: string;
  artisanSlug?: string;
  twoFactorEnabled: boolean;
}

export interface TwoFactorChallenge {
  requires2FA: true;
  tempToken: string;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isBuyer: boolean;
  isArtisan: boolean;
  isAdmin: boolean;
  canShop: boolean;
  login: (email: string, password: string) => Promise<User | TwoFactorChallenge>;
  loginWithGoogle: (idToken: string) => Promise<User>;
  verifyTwoFactor: (tempToken: string, code: string) => Promise<User>;
  logout: (message?: string) => Promise<void>;
  setTwoFactorEnabled: (enabled: boolean) => void;
  updateUserInfo: (patch: Partial<Pick<User, 'firstName' | 'lastName'>>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'nimba_auth_user';

function loadUser(): User | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function saveUser(user: User | null) {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function mapBackendUser(backendUser: any): User {
  return {
    id: backendUser.code ?? String(backendUser.id),
    email: backendUser.email,
    firstName: backendUser.prenom,
    lastName: backendUser.nom,
    role: backendUser.role,
    artisanSlug: backendUser.role === 'vendeur' ? backendUser.code : undefined,
    twoFactorEnabled: Boolean(backendUser.two_factor_confirmed_at),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(loadUser);
  const { addToast } = useToast();

  useEffect(() => {
    saveUser(user);
  }, [user]);

  const login = useCallback(async (email: string, password: string): Promise<User | TwoFactorChallenge> => {
    try {
      const data = await loginApi({ email, password });

      // L'admin avec 2FA active recoit un temp_token au lieu du token final —
      // il faut passer par /auth/2fa/verify avant d'etre reellement connecte.
      if (data.requires_2fa) {
        return { requires2FA: true, tempToken: data.temp_token };
      }

      const loggedUser = mapBackendUser(data.user);
      localStorage.setItem('token', data.token);
      setUser(loggedUser);
      return loggedUser;
    } catch (error: any) {
      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 401) {
        throw new Error(message || 'Email ou mot de passe incorrect.');
      }
      if (status === 422) {
        const serverErrors = error.response.data.errors as Record<string, string[]>;
        throw new Error(Object.values(serverErrors)[0]?.[0] || message || 'Donnees invalides.');
      }
      throw new Error(message || 'Erreur de connexion. Veuillez reessayer.');
    }
  }, []);

  const verifyTwoFactor = useCallback(async (tempToken: string, code: string): Promise<User> => {
    try {
      const data = await verify2FAApi(tempToken, code);
      const loggedUser = mapBackendUser(data.user);
      localStorage.setItem('token', data.token);
      setUser(loggedUser);
      return loggedUser;
    } catch (error: any) {
      const status = error.response?.status;
      const message = error.response?.data?.message;
      if (status === 422 || status === 401) {
        throw new Error(message || 'Code invalide ou expire.');
      }
      throw new Error(message || 'Erreur de verification. Veuillez reessayer.');
    }
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string): Promise<User> => {
    try {
      const data = await googleLoginApi(idToken);
      const loggedUser = mapBackendUser(data.user);
      localStorage.setItem('token', data.token);
      setUser(loggedUser);
      return loggedUser;
    } catch (error: any) {
      const status = error.response?.status;
      const message = error.response?.data?.message;
      if (status === 404 || status === 422 || status === 401) {
        throw new Error(message || 'Aucun compte associe a ce compte Google.');
      }
      throw new Error(message || 'Erreur de connexion avec Google. Veuillez reessayer.');
    }
  }, []);

  // Met a jour localement l'etat 2FA juste apres une activation confirmee —
  // pas d'endpoint "me" pour rafraichir tout l'utilisateur, donc on reflete l'etat connu.
  const setTwoFactorEnabled = useCallback((enabled: boolean) => {
    setUser((prev) => (prev ? { ...prev, twoFactorEnabled: enabled } : prev));
  }, []);

  // Reflet local apres la sauvegarde de "Mon profil" — meme logique que setTwoFactorEnabled,
  // pas d'endpoint "me" pour tout re-synchroniser depuis le serveur.
  const updateUserInfo = useCallback((patch: Partial<Pick<User, 'firstName' | 'lastName'>>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const logout = useCallback(async (message: string = 'Deconnecte. A bientot !') => {
    await disablePushNotifications();
    try {
      await logoutApi();
    } catch {
      // best-effort : la deconnexion locale doit toujours reussir meme si l'appel serveur echoue
    }
    setUser(null);
    localStorage.removeItem('token');
    addToast({ type: 'info', message });
  }, [addToast]);

  const isAuthenticated = user !== null;
  usePushNotifications(isAuthenticated);
  const isBuyer = user?.role === 'acheteur';
  const isArtisan = user?.role === 'vendeur';
  const isAdmin = user?.role === 'admin';
  // L'artisan peut aussi commander comme un acheteur depuis son interface (point 5.2 du cahier de correction).
  const canShop = isBuyer || isArtisan;

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      return;
    }

    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        logout('Session expiree pour inactivite. Veuillez vous reconnecter.');
      }, INACTIVITY_LIMIT_MS);
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer));

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [isAuthenticated, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isBuyer,
        isArtisan,
        isAdmin,
        canShop,
        login,
        loginWithGoogle,
        verifyTwoFactor,
        logout,
        setTwoFactorEnabled,
        updateUserInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}